import { cn } from "@/lib/utils";

/**
 * SRG Consulting PLLC — Reusable Master Logo Component
 *
 * Single source of truth for the brand mark across all pages.
 * Renders the authoritative sculpted mark provided in brand assets:
 * https://vibe.filesafe.space/1787785978701289352/attachments/f37a5878-9870-471e-9b7c-65afdd27b3ef.png
 *
 * Mark features:
 * - Dimensional midnight-navy serif lettering "SRG" with sculpted gold/champagne bevels
 * - Flanked gold rule divider beneath
 * - Refined tracked "CONSULTING PLLC" wordmark in deep editorial ink
 *
 * Theme / Surface awareness:
 * - On Ivory / Cream backgrounds (default light mode): uses `mix-blend-multiply`
 *   to blend the clean white backing perfectly into the warm ivory (#F4F0E6) ground,
 *   letting the deep navy and gold bevels stand out cleanly.
 * - On Navy backgrounds (e.g. footer, dark hero, dark mode): rendered with an elegant
 *   luminous ivory plaque substrate and subtle gold-ruled rim, keeping the sculpted
 *   typography readable and prestigious without color clipping.
 * - Standalone `monogram` option: renders the clean "S" seal / favicon for tight spaces.
 */

export const SRG_LOGO_URL =
  "https://vibe.filesafe.space/1787785978701289352/attachments/f37a5878-9870-471e-9b7c-65afdd27b3ef.png";

export type LogoSize = "sm" | "md" | "lg" | "xl";
export type LogoVariant = "auto" | "light" | "dark";

const SIZE_CONTAINER: Record<LogoSize, string> = {
  sm: "h-7 sm:h-8",
  md: "h-9 sm:h-11",
  lg: "h-14 sm:h-16",
  xl: "h-20 sm:h-24",
};

const SIZE_IMG: Record<LogoSize, string> = {
  sm: "h-7 sm:h-8 max-w-[140px]",
  md: "h-9 sm:h-11 max-w-[210px]",
  lg: "h-14 sm:h-16 max-w-[300px]",
  xl: "h-20 sm:h-24 max-w-[420px]",
};

const ALT =
  "SRG Consulting PLLC — Business Strategy, Wealth Preservation, Capital Solutions";

export interface LogoProps {
  size?: LogoSize;
  /**
   * "auto" adapts to theme. "light" renders for ivory/cream grounds.
   * "dark" renders on dark/navy surfaces (e.g. footer on navy #0E1E3C).
   */
  variant?: LogoVariant;
  /** Render the standalone "S" monogram seal (favicon) instead of the full lockup. */
  monogram?: boolean;
  className?: string;
  imgClassName?: string;
}

export function Logo({
  size = "md",
  variant = "auto",
  monogram = false,
  className,
  imgClassName,
}: LogoProps) {
  if (monogram) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center shrink-0",
          SIZE_CONTAINER[size],
          className,
        )}
        role="img"
        aria-label={ALT}
      >
        <img
          src="/favicon.svg"
          alt="SRG Monogram Seal"
          className={cn(
            "w-auto h-full object-contain drop-shadow-[0_2px_8px_rgba(14,30,60,0.18)] transition-transform duration-300",
            imgClassName,
          )}
          loading="eager"
          decoding="async"
        />
      </span>
    );
  }

  const containerClass = SIZE_CONTAINER[size];
  const imgClass = SIZE_IMG[size];

  // When forced for dark/navy backgrounds (e.g. footer on architectural navy #0E1E3C)
  if (variant === "dark") {
    return (
      <span
        className={cn(
          "inline-flex items-center relative group/logo",
          containerClass,
          className,
        )}
        role="img"
        aria-label={ALT}
      >
        {/* Luminous ivory substrate framed with gold rule so the sculpted navy/gold emblem sits cleanly on navy */}
        <span className="relative inline-flex items-center bg-[#F4F0E6] border border-[#B08D57]/50 rounded-md px-2.5 py-1 shadow-[0_4px_16px_rgba(0,0,0,0.25)] ring-1 ring-[#C9A86A]/30">
          <img
            src={SRG_LOGO_URL}
            alt={ALT}
            className={cn(
              "w-auto object-contain mix-blend-multiply",
              imgClass,
              imgClassName,
            )}
            loading="eager"
            decoding="async"
          />
        </span>
      </span>
    );
  }

  // Default theme-aware presentation:
  // On ivory/cream backgrounds: mix-blend-multiply blends seamlessly into #F4F0E6
  // In dark mode: adapt gracefully with luminous ivory plaque
  return (
    <span
      className={cn(
        "inline-flex items-center relative",
        containerClass,
        className,
      )}
      role="img"
      aria-label={ALT}
    >
      <img
        src={SRG_LOGO_URL}
        alt={ALT}
        className={cn(
          "w-auto object-contain mix-blend-multiply filter contrast-[1.04]",
          imgClass,
          variant === "auto" && "dark:hidden",
          imgClassName,
        )}
        loading="eager"
        decoding="async"
      />

      {variant === "auto" && (
        <span className="hidden dark:inline-flex items-center bg-[#F4F0E6] border border-[#B08D57]/50 rounded-md px-2 py-0.5 shadow-md">
          <img
            src={SRG_LOGO_URL}
            alt={ALT}
            className={cn(
              "w-auto object-contain mix-blend-multiply filter contrast-[1.04]",
              imgClass,
              imgClassName,
            )}
            loading="eager"
            decoding="async"
          />
        </span>
      )}
    </span>
  );
}

export default Logo;
